import React, { useId, useState } from 'react'

type Variant = 'text' | 'email' | 'password'

interface AppTextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string
  error?: string
  variant?: Variant
}

/**
 * Themed text input with a floating-style label, accessible error display,
 * and support for password reveal. Used by the redesigned login surfaces.
 *
 * Wraps the existing react-hook-form `register()` props pattern — the form
 * library handles validation; this component handles only presentation.
 */
export const AppTextField = React.forwardRef<HTMLInputElement, AppTextFieldProps>(
  function AppTextField({ label, error, variant = 'text', id, className = '', ...rest }, ref) {
    const fallbackId = useId()
    const inputId = id ?? fallbackId
    const [show, setShow] = useState(false)

    const isPassword = variant === 'password'
    const inputType = isPassword ? (show ? 'text' : 'password') : variant

    const errorState = Boolean(error)

    return (
      <div className={`flex flex-col gap-1.5 ${className}`}>
        <label
          htmlFor={inputId}
          className="text-xs font-medium uppercase tracking-wider text-[color:var(--ink-soft)]"
        >
          {label}
        </label>
        <div className="relative">
          <input
            id={inputId}
            ref={ref}
            type={inputType}
            aria-invalid={errorState || undefined}
            aria-describedby={errorState ? `${inputId}-error` : undefined}
            className={[
              'w-full px-4 py-3.5',
              'bg-[color:var(--surface)]',
              'border',
              errorState
                ? 'border-[color:var(--negative)] focus:border-[color:var(--negative)]'
                : 'border-[color:var(--border-strong)] focus:border-[color:var(--accent)]',
              'rounded-[var(--radius-input)]',
              'text-base text-[color:var(--ink)] placeholder:text-[color:var(--ink-faint)]',
              'outline-none transition-colors',
              'focus:ring-2 focus:ring-[color:var(--accent)]/15',
              isPassword ? 'pr-12' : '',
            ].join(' ')}
            {...rest}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute inset-y-0 right-0 px-4 text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
              aria-label={show ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {show ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.6}
                    d="M3.98 8.223A10.477 10.477 0 001.934 12c1.292 4.338 5.31 7.5 10.066 7.5 1.638 0 3.197-.376 4.587-1.046M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.6}
                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.6}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </button>
          )}
        </div>
        {errorState && (
          <p
            id={`${inputId}-error`}
            className="text-xs text-[color:var(--negative)] mt-0.5"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

export default AppTextField
